// sheets.jsx — Bottom-sheet primitive + contextual sheet content.
// Sheets slide up over the phone screen to expose secondary actions
// without losing the active-workout context.

import React from 'react';
import { createPortal } from 'react-dom';
import { EXERCISE_NOTES, INTENSITY_TECHNIQUES } from './data.jsx';
import { LL, LLChip, LLClose, LLIcon, LLKicker, LLNum } from './tokens.jsx';
import { parseYouTube } from './utils.jsx';
import { useSheetHistory } from './sheet_history.js';

// Sheets render through a portal into a viewport-fixed layer at <body> level:
// a sheet declared inside a scrolled container (review/summary, long screens)
// must anchor to the screen, not to the top of the scroll content.
function SheetLayer({ children }) {
  if (typeof document === 'undefined') return children;
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 150, pointerEvents: 'none' }}>{children}</div>,
    document.body,
  );
}

function LLSheet(props) {
  return <SheetLayer><LLSheetBody {...props} /></SheetLayer>;
}

function LLSheetBody({ open, title, subtitle, onClose, children, maxHeight = '80%' }) {
  const sheetRef = React.useRef(null);
  // Android/browser Back closes the sheet (not the screen behind it).
  useSheetHistory(open, onClose);

  // Keyboard-safety guard (INPUT-05): when the on-screen keyboard opens, push
  // the sheet up so the focused field and its steppers stay above the keyboard.
  // Uses visualViewport because the phone-frame container does NOT shrink on
  // keyboard open — position:absolute/bottom:0 requires a direct style.bottom
  // mutation (paddingBottom would be wrong for this layout; scroll-into-view
  // fails inside the frame — see 37-RESEARCH.md Pitfall A & B).
  React.useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return; // graceful no-op on desktop / unsupported browsers

    function handleVV() {
      const inset = window.innerHeight - vv.offsetTop - vv.height;
      if (sheetRef.current) {
        sheetRef.current.style.bottom = inset > 0 ? inset + 'px' : '0px';
        sheetRef.current.style.transition = 'bottom 0.15s ease';
      }
    }

    vv.addEventListener('resize', handleVV);
    vv.addEventListener('scroll', handleVV);

    return () => {
      vv.removeEventListener('resize', handleVV);
      vv.removeEventListener('scroll', handleVV);
      if (sheetRef.current) {
        sheetRef.current.style.bottom = '0px';
        sheetRef.current.style.transition = '';
      }
    };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'absolute', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
        transition: 'opacity .22s',
      }} />
      {/* Sheet */}
      <div ref={sheetRef} data-ll-sheet={open ? 'open' : undefined} style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 101,
        background: LL.surface, borderTopLeftRadius: LL.r.lg, borderTopRightRadius: LL.r.lg,
        border: `1px solid ${LL.line}`, borderBottom: 'none',
        maxHeight, display: 'flex', flexDirection: 'column',
        transform: open ? 'translateY(0)' : 'translateY(105%)',
        // When closed, fully hide + disable pointers so a "closed" sheet can't
        // paint over the screen or swallow taps meant for the screen behind it
        // (its containing block isn't always full-height, so translateY alone
        // doesn't reliably move it off-screen). visibility flips to hidden only
        // after the slide-out finishes so the close animation stays visible.
        visibility: open ? 'visible' : 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        transition: open
          ? 'transform .28s cubic-bezier(.2,.8,.25,1), visibility 0s'
          : 'transform .28s cubic-bezier(.2,.8,.25,1), visibility 0s linear .28s',
        boxShadow: LL.shadow.sheet,
      }}>
        <div style={{ padding: '10px 0 0', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 40, height: 4, borderRadius: LL.r.xs, background: LL.line2 }} />
        </div>
        {title && (
          <div style={{ padding: '10px 18px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              {subtitle && <LLKicker>{subtitle}</LLKicker>}
              <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, letterSpacing: -0.3, color: LL.fg, marginTop: subtitle ? 2 : 0 }}>{title}</div>
            </div>
            <LLClose onClick={onClose} />
          </div>
        )}
        <div style={{ flex: 1, overflow: 'auto', padding: '4px 18px 20px' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ————— Exercise menu sheet —————
// Main hub for an exercise: notes, video, intensity, swap, substitute,
// reorder, skip. Accessed via the "···" button on the exercise card.
function ExerciseMenuSheet({ ex, open, onClose, onAction }) {
  if (!ex) return null;
  const actions = [
    { id: 'notes',      icon: LLIcon.note,   label: 'Notes & cues',        tint: LL.fg,    sub: 'Form cues, today\'s note, history' },
    { id: 'intensity',  icon: LLIcon.flame,  label: 'Intensity technique', tint: LL.pr,    sub: 'Partials, drop set, rest-pause…' },
    { id: 'video',      icon: LLIcon.video,  label: 'Watch video',         tint: LL.fg,    sub: 'Linked YouTube demo' },
    { id: 'plate',      icon: LLIcon.dumbbell, label: 'Plate calculator', tint: LL.fg,    sub: 'How to load the bar' },
    { id: 'warmup',     icon: LLIcon.timer,  label: 'Warm-up ramp',        tint: LL.fg,    sub: 'Auto-calc warm-up sets' },
    { id: 'swap',       icon: LLIcon.swap,   label: 'Substitute exercise', tint: LL.fg,    sub: 'Machine busy? Pick an alt' },
    { id: 'skip',       icon: LLIcon.chev,   label: 'Skip for now',        tint: LL.warn,  sub: 'Defer to end of workout' },
    { id: 'superset',   icon: LLIcon.grip,   label: 'Superset with…',      tint: LL.fg,    sub: 'Pair with another exercise' },
  ];
  return (
    <LLSheet open={open} onClose={onClose} title={ex.name} subtitle={`${ex.group.toUpperCase()} · ${ex.sets} × ${ex.repMin}–${ex.repMax}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {actions.map(a => (
          <button key={a.id} onClick={() => onAction(a.id)} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '11px 12px', borderRadius: LL.r.md,
            background: LL.surface2, border: `1px solid ${LL.line}`,
            color: LL.fg, textAlign: 'left', cursor: 'pointer',
            fontFamily: LL.ui,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: LL.r.sm,
              background: LL.bg, color: a.tint,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><a.icon /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg }}>{a.label}</div>
              <div style={{ fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 1 }}>{a.sub}</div>
            </div>
            <LLIcon.chev style={{ color: LL.fgFaint }} />
          </button>
        ))}
      </div>
    </LLSheet>
  );
}

// ————— Notes sheet —————
// Three tiers: pinned (form cues), today's session note, history.
function NotesSheet({ ex, open, onClose }) {
  const [tab, setTab] = React.useState('today');
  const [sessionNote, setSessionNote] = React.useState('');
  if (!ex) return null;
  const data = EXERCISE_NOTES[ex.name] || { pinned: '', cues: [], history: [], substitutes: [] };

  return (
    <LLSheet open={open} onClose={onClose} title="Notes & cues" subtitle={ex.name.toUpperCase()} maxHeight="85%">
      {/* Pinned card, always visible */}
      <div style={{
        padding: 12, borderRadius: LL.r.md,
        background: `color-mix(in oklab, ${LL.accent} 10%, ${LL.surface2})`,
        border: `1px solid color-mix(in oklab, ${LL.accent} 35%, ${LL.line})`,
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill={LL.accent}><path d="M8 1l2 4 4.5.5-3.3 3L12 13 8 10.8 4 13l.8-4.5L1.5 5.5 6 5z"/></svg>
          <LLKicker style={{ color: LL.accent }}>PINNED · FORM CUE</LLKicker>
        </div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.45, textWrap: 'pretty' }}>
          {data.pinned || 'No pinned cues yet. Tap + to add one.'}
        </div>
        {data.cues?.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
            {data.cues.map(c => <LLChip key={c} tone="outline" size="sm">{c}</LLChip>)}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, padding: 3, background: LL.bg, borderRadius: LL.r.md, border: `1px solid ${LL.line}` }}>
        {[
          { id: 'today', label: 'Today' },
          { id: 'history', label: `History · ${data.history?.length || 0}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '8px 10px', borderRadius: LL.r.sm,
            background: tab === t.id ? LL.surface3 : 'transparent',
            color: tab === t.id ? LL.fg : LL.fgDim,
            border: 'none', fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600,
            cursor: 'pointer',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'today' && (
        <div>
          <LLKicker style={{ marginBottom: 6 }}>TODAY&rsquo;S NOTE</LLKicker>
          <textarea
            value={sessionNote}
            onChange={e => setSessionNote(e.target.value)}
            placeholder="Log form notes, pain flags, feel, equipment notes — only for this session."
            style={{
              width: '100%', minHeight: 110,
              padding: 12, borderRadius: LL.r.md,
              background: LL.bg, border: `1px solid ${LL.line}`,
              color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, lineHeight: 1.45,
              resize: 'none', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
            {['Felt strong', 'Form break', 'Pain flag', 'Grip failed', 'Deload', 'Left reps'].map(t => (
              <button key={t} onClick={() => setSessionNote(n => (n ? n + ' · ' : '') + t)} style={{
                padding: '5px 10px', borderRadius: LL.r.pill,
                background: LL.surface2, border: `1px solid ${LL.line}`,
                color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, cursor: 'pointer',
              }}>+ {t}</button>
            ))}
          </div>
          <button style={{
            marginTop: 12, width: '100%', height: 42, borderRadius: LL.r.md,
            background: LL.accent, color: LL.accentInk, border: 'none',
            fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
          }}>Save note</button>
        </div>
      )}

      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.history?.length > 0 ? data.history.map((h, i) => (
            <div key={i} style={{ padding: 12, borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgDim }}>{h.date}</span>
                <span style={{ fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgFaint }}>·</span>
                <span style={{ fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgDim }}>{h.session}</span>
                <div style={{ flex: 1 }} />
                {h.tag && <LLChip tone={h.tag === 'PR' ? 'warm' : 'outline'} size="sm">{h.tag}</LLChip>}
              </div>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.45, textWrap: 'pretty' }}>
                {h.body}
              </div>
            </div>
          )) : (
            <div style={{ padding: 22, textAlign: 'center', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim }}>
              No past notes for this exercise yet.
            </div>
          )}
        </div>
      )}
    </LLSheet>
  );
}

// ————— Intensity technique sheet —————
function IntensitySheet({ ex, setIdx, currentTech, open, onClose, onPick }) {
  if (!ex) return null;
  const techs = INTENSITY_TECHNIQUES;
  return (
    <LLSheet open={open} onClose={onClose} title="Intensity technique" subtitle={`SET ${setIdx + 1} · ${ex.name.toUpperCase()}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {techs.map(t => {
          const on = t.id === currentTech;
          const color = t.color === 'pr' ? LL.pr : t.color === 'info' ? LL.info : t.color === 'mute' ? LL.fgMute : LL.accent;
          return (
            <button key={t.id} onClick={() => onPick(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '11px 12px', borderRadius: LL.r.md,
              background: on ? LL.surface3 : LL.surface2,
              border: `1.5px solid ${on ? color : LL.line}`,
              color: LL.fg, textAlign: 'left', cursor: 'pointer',
              fontFamily: LL.ui,
            }}>
              <div style={{
                minWidth: 54, padding: '4px 8px', borderRadius: LL.r.sm,
                background: on ? color : LL.bg,
                color: on ? (t.color === 'mute' ? LL.fg : LL.accentInk) : color,
                fontFamily: LL.display, fontSize: LL.fs.sm, fontWeight: 700, letterSpacing: 0.5,
                textAlign: 'center', textTransform: 'uppercase', flexShrink: 0,
              }}>{t.abbr}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg }}>{t.name}</div>
                <div style={{ fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 1, lineHeight: 1.35 }}>{t.desc}</div>
              </div>
              <div style={{
                width: 18, height: 18, borderRadius: LL.r.sm,
                border: `1.5px solid ${on ? color : LL.line2}`,
                background: on ? color : 'transparent',
                flexShrink: 0,
              }} />
            </button>
          );
        })}
      </div>
    </LLSheet>
  );
}

// ————— Plate calculator sheet —————
function PlateCalcSheet({ ex, open, onClose }) {
  const [weight, setWeight] = React.useState(ex?.target?.w || 225);
  React.useEffect(() => { if (ex) setWeight(ex.target.w); }, [ex]);
  const bar = 45;
  const perSide = Math.max(0, (weight - bar) / 2);
  const plateSizes = [45, 35, 25, 10, 5, 2.5];
  const plates = [];
  let remain = perSide;
  plateSizes.forEach(p => {
    while (remain >= p - 0.001) {
      plates.push(p);
      remain -= p;
    }
  });
  const residual = remain > 0.1;

  const plateColor = (p) => {
    if (p === 45) return { bg: LL.fg, fg: LL.bg };
    if (p === 35) return { bg: LL.pr, fg: LL.bg };
    if (p === 25) return { bg: LL.info, fg: LL.bg };
    if (p === 10) return { bg: LL.accent, fg: LL.accentInk };
    if (p === 5) return { bg: LL.fgMute, fg: LL.bg };
    return { bg: LL.surface3, fg: LL.fg };
  };
  const plateH = (p) => 100 * (0.55 + (p / 45) * 0.45);

  return (
    <LLSheet open={open} onClose={onClose} title="Plate calculator" subtitle="45 LB BAR · LB">
      <div style={{ padding: 14, background: LL.surface2, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, marginBottom: 12 }}>
        <LLKicker>TARGET</LLKicker>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <button onClick={() => setWeight(w => Math.max(45, w - 5))} style={{ width: 36, height: 36, borderRadius: LL.r.sm, background: LL.surface3, border: 'none', color: LL.fg, fontSize: LL.fs.lg }}>−</button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <LLNum value={weight} unit="lb" size={40} />
          </div>
          <button onClick={() => setWeight(w => w + 5)} style={{ width: 36, height: 36, borderRadius: LL.r.sm, background: LL.accent, border: 'none', color: LL.accentInk, fontSize: LL.fs.lg }}>+</button>
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 8, justifyContent: 'center' }}>
          {[5, 10, 25].map(d => (
            <React.Fragment key={d}>
              <button onClick={() => setWeight(w => Math.max(45, w - d))} style={{ padding: '4px 9px', borderRadius: LL.r.sm, background: 'transparent', border: `1px solid ${LL.line}`, color: LL.fgMute, fontFamily: LL.mono, fontSize: LL.fs.cap }}>−{d}</button>
              <button onClick={() => setWeight(w => w + d)} style={{ padding: '4px 9px', borderRadius: LL.r.sm, background: 'transparent', border: `1px solid ${LL.line}`, color: LL.fgMute, fontFamily: LL.mono, fontSize: LL.fs.cap }}>+{d}</button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Bar diagram */}
      <LLKicker style={{ marginBottom: 8 }}>LOAD · ONE SIDE</LLKicker>
      <div style={{
        padding: '24px 10px', borderRadius: LL.r.md,
        background: LL.bg, border: `1px solid ${LL.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
        minHeight: 140, position: 'relative', overflow: 'hidden',
      }}>
        {/* sleeve */}
        <div style={{ position: 'absolute', left: '50%', right: 0, top: '50%', height: 14, background: LL.fgFaint, transform: 'translateY(-50%)', borderRadius: `${LL.r.xs / 2}px ${LL.r.xs}px ${LL.r.xs}px ${LL.r.xs / 2}px` /* token-ok: bar sleeve */ }} />
        {/* collar end */}
        <div style={{ position: 'absolute', right: 8, top: '50%', width: 14, height: 28, background: LL.fgMute, transform: 'translateY(-50%)', borderRadius: LL.r.xs }} />
        <div style={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
          {plates.map((p, i) => {
            const c = plateColor(p);
            return (
              <div key={i} style={{
                width: p === 45 ? 18 : p === 35 ? 16 : p === 25 ? 14 : p === 10 ? 11 : p === 5 ? 9 : 7,
                height: plateH(p),
                background: c.bg, color: c.fg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: LL.display, fontSize: LL.fs.cap, fontWeight: 700,
                borderRadius: LL.r.xs,
                writingMode: 'vertical-rl', textOrientation: 'mixed',
              }}>{p}</div>
            );
          })}
          {plates.length === 0 && (
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim, padding: '10px 4px' }}>Bar only</div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
        {Object.entries(plates.reduce((a, p) => { a[p] = (a[p] || 0) + 1; return a; }, {})).map(([p, n]) => (
          <LLChip key={p} tone="outline" size="md">{n}× {p}</LLChip>
        ))}
        {residual && <LLChip tone="warm" size="md">+{remain.toFixed(1)} lb short</LLChip>}
      </div>
    </LLSheet>
  );
}

// ————— Warm-up ramp sheet —————
function WarmupSheet({ ex, open, onClose }) {
  const working = ex?.target?.w || 0;
  // Standard pyramid: empty bar, 40%, 60%, 75%, 90%
  const ramp = [
    { pct: 0,    w: 45,   reps: 10, note: 'Bar only · mobility' },
    { pct: 0.40, w: Math.round(working * 0.40 / 5) * 5, reps: 8, note: '' },
    { pct: 0.60, w: Math.round(working * 0.60 / 5) * 5, reps: 5, note: '' },
    { pct: 0.80, w: Math.round(working * 0.80 / 5) * 5, reps: 3, note: 'Last warm-up' },
  ].filter(r => r.w > 0);
  return (
    <LLSheet open={open} onClose={onClose} title="Warm-up ramp" subtitle={ex ? `TARGET: ${working} LB × ${ex.target.r}` : ''}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ramp.map((r, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '11px 12px', borderRadius: LL.r.md,
            background: LL.surface2, border: `1px solid ${LL.line}`,
          }}>
            <div style={{ fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgDim, width: 42 }}>
              {r.pct ? `${Math.round(r.pct*100)}%` : 'warm'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg }}>{r.w}</span>
                <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim, textTransform: 'uppercase' }}>lb</span>
                <span style={{ fontFamily: LL.mono, fontSize: LL.fs.sm, color: LL.fgMute, marginLeft: 6 }}>× {r.reps}</span>
              </div>
              {r.note && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 1 }}>{r.note}</div>}
            </div>
            <button style={{
              padding: '6px 10px', borderRadius: LL.r.sm, border: `1px solid ${LL.line}`,
              background: 'transparent', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600,
            }}>Log</button>
          </div>
        ))}
        <div style={{ padding: '10px 12px', borderRadius: LL.r.md, background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, marginTop: 4 }}>
          Then: Working {working} lb × {ex?.target?.r}
        </div>
      </div>
    </LLSheet>
  );
}

// ————— Substitute exercise sheet —————
function SubstituteSheet({ pe, alternates, currentVariantName, open, onClose, onPick, onOpenVideo, primaryYt }) {
  if (!pe) return null;
  const rows = [
    // primaryYt is the parent-resolved url (pe.yt || linked catalog exercise's yt),
    // matching the workout header's video resolution so the primary glyph is consistent.
    { name: pe.name, yt: primaryYt || pe.yt || '', isPrimary: true },
    ...(alternates || []).map(a => ({ name: a.name, yt: a.yt || '', isPrimary: false })),
  ];
  return (
    <LLSheet open={open} onClose={onClose} title="Substitute exercise" subtitle={`REPLACE: ${pe.name.toUpperCase()}`}>
      <div style={{
        padding: 12, borderRadius: LL.r.md, marginBottom: 12,
        background: `color-mix(in oklab, ${LL.warn} 12%, ${LL.surface2})`,
        border: `1px solid color-mix(in oklab, ${LL.warn} 30%, ${LL.line})`,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill={LL.warn} style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M8 1l7 13H1z" fillOpacity="0.25"/>
          <path d="M8 5v4M8 11v0.5" stroke={LL.warn} strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, lineHeight: 1.45 }}>
          Substitution applies to this session only. Your program stays unchanged.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((row, idx) => {
          const parsedAlt = parseYouTube(row.yt);
          const isActive = row.isPrimary
            ? currentVariantName === null
            : currentVariantName === row.name;
          return (
            <div key={row.isPrimary ? '__primary' : 'alt-' + idx} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 14px', borderRadius: LL.r.md,
              background: isActive ? LL.surface3 : LL.surface2,
              border: `1.5px solid ${isActive ? LL.accent : LL.line}`,
            }}>
              <button
                onClick={() => onPick(row.isPrimary ? null : { name: row.name, yt: row.yt })}
                style={{ flex: 1, background: 'none', border: 'none', textAlign: 'left',
                  color: LL.fg, fontFamily: LL.ui, padding: 0, cursor: 'pointer' }}>
                <div style={{ fontSize: LL.fs.meta, fontWeight: 600 }}>{row.name}</div>
                {row.isPrimary && (
                  <div style={{ fontSize: LL.fs.cap, color: LL.fgDim, fontFamily: LL.mono, marginTop: 2 }}>
                    PRIMARY
                  </div>
                )}
              </button>
              {parsedAlt && (
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenVideo(row.yt, row.name); }}
                  style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer',
                    color: LL.fgMute }}>
                  <LLIcon.video style={{ width: 16, height: 16 }} />
                </button>
              )}
              {isActive && <LLIcon.check style={{ color: LL.accent, flexShrink: 0 }} />}
            </div>
          );
        })}
      </div>
    </LLSheet>
  );
}

// ————— Superset picker sheet —————
function SupersetSheet({ ex, exList, activeIdx, open, onClose, onPair }) {
  if (!ex) return null;
  const candidates = exList.map((e, i) => ({ e, i })).filter(x => x.i !== activeIdx);
  return (
    <LLSheet open={open} onClose={onClose} title="Superset with…" subtitle={`PAIR WITH: ${ex.name.toUpperCase()}`}>
      <div style={{
        padding: 12, borderRadius: LL.r.md, marginBottom: 12,
        background: LL.surface2, border: `1px solid ${LL.line}`,
      }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fg, lineHeight: 1.45 }}>
          Sets will alternate between the two exercises. Rest happens only between pairs.
          <div style={{ marginTop: 6, fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgDim }}>
            Good for: antagonist pairs, time-saving, time-under-tension.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {candidates.map(({ e, i }) => (
          <button key={e.id} onClick={() => onPair(i)} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '11px 12px', borderRadius: LL.r.md,
            background: LL.surface2, border: `1px solid ${LL.line}`,
            color: LL.fg, textAlign: 'left', cursor: 'pointer', fontFamily: LL.ui,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: LL.r.sm, background: LL.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: LL.mono, fontSize: LL.fs.cap, fontWeight: 700, color: LL.fgMute, flexShrink: 0,
            }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</div>
              <div style={{ fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 1 }}>{e.group} · {e.sets} × {e.repMin}–{e.repMax}</div>
            </div>
            <LLIcon.chev style={{ color: LL.fgFaint }} />
          </button>
        ))}
      </div>
    </LLSheet>
  );
}

// ————— Weight edit sheet —————
// Inline numeric keypad + "apply to remaining sets" toggle.
function WeightEditSheet({ ex, setIdx, currentWeight, open, onClose, onApply }) {
  const [value, setValue] = React.useState(currentWeight);
  const [applyAll, setApplyAll] = React.useState(true);
  React.useEffect(() => { setValue(currentWeight); }, [currentWeight, open]);
  if (!ex) return null;
  const last = ex.last?.w;
  const target = ex.target?.w;

  const pad = [1,2,3,4,5,6,7,8,9,'.5',0,'⌫'];
  const press = (k) => {
    if (k === '⌫') {
      setValue(v => Math.floor(v / 10));
    } else if (k === '.5') {
      setValue(v => v + 0.5);
    } else {
      setValue(v => v * 10 + k);
    }
  };

  return (
    <LLSheet open={open} onClose={onClose} title="Edit weight" subtitle={`SET ${setIdx + 1} · ${ex.name.toUpperCase()}`}>
      <div style={{
        padding: 18, borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}`,
        textAlign: 'center', marginBottom: 10,
      }}>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.hero, fontWeight: 700, color: LL.fg, letterSpacing: -1, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim, textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 }}>LB</div>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 10 }}>
          {[
            { label: `Last: ${last}`, v: last },
            { label: `Target: ${target}`, v: target },
            { label: '+5', v: value + 5 },
            { label: '−5', v: value - 5 },
          ].map(b => (
            <button key={b.label} onClick={() => setValue(Math.max(0, b.v))} style={{
              padding: '5px 10px', borderRadius: LL.r.sm,
              background: LL.bg, border: `1px solid ${LL.line}`,
              color: LL.fgMute, fontFamily: LL.mono, fontSize: LL.fs.cap, fontWeight: 600, cursor: 'pointer',
            }}>{b.label}</button>
          ))}
        </div>
      </div>

      {/* keypad */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 }}>
        {pad.map(k => (
          <button key={k} onClick={() => press(k)} style={{
            height: 48, borderRadius: LL.r.md,
            background: k === '⌫' ? LL.surface3 : LL.surface2,
            border: `1px solid ${LL.line}`, color: LL.fg,
            fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, cursor: 'pointer',
          }}>{k}</button>
        ))}
      </div>

      {/* apply to remaining toggle */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 12px', borderRadius: LL.r.md,
        background: LL.surface2, border: `1px solid ${LL.line}`,
        fontFamily: LL.ui, cursor: 'pointer', marginBottom: 10,
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: LL.r.sm,
          border: `1.5px solid ${applyAll ? LL.accent : LL.line2}`,
          background: applyAll ? LL.accent : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: LL.accentInk, flexShrink: 0,
        }} onClick={(e) => { e.preventDefault(); setApplyAll(a => !a); }}>
          {applyAll && <LLIcon.check style={{ width: 14, height: 14 }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg }}>Apply to remaining sets</div>
          <div style={{ fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 1 }}>Updates this and all unlogged sets below</div>
        </div>
      </label>

      <button onClick={() => { onApply(value, applyAll); onClose(); }} style={{
        width: '100%', height: 48, borderRadius: LL.r.md,
        background: LL.accent, color: LL.accentInk, border: 'none',
        fontFamily: LL.display, fontSize: LL.fs.btn, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      }}>Update</button>
    </LLSheet>
  );
}

// ————— Video player sheet —————
// Embeds a youtube-nocookie iframe inside an LLSheet.
// The iframe is ONLY rendered when open===true and parsed is non-null (VID-04
// lazy-mount guard). LLSheet uses visibility:hidden when closed but does NOT
// unmount children — the conditional here is the actual gate. Closing the sheet
// flips open→false, the conditional removes the iframe from the DOM, and
// playback/audio stop.
// Offline: shows "Internet required" + Open-in-YouTube fallback link.
// Fullscreen: calls requestFullscreen on the iframe wrapper; iOS Safari rejects
// (it only supports fullscreen on <video> elements) — the .catch() swallows the
// rejection and YouTube's own fullscreen button (via allow="fullscreen") handles iOS.
function VideoPlayerSheet({ open, onClose, videoUrl, exerciseName }) {
  // Memoize the parsed result to avoid re-parsing on every render.
  const parsed = React.useMemo(() => parseYouTube(videoUrl), [videoUrl]);
  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine);
  const iframeRef = React.useRef(null);

  // Subscribe to connectivity changes so an open player can update reactively.
  React.useEffect(() => {
    const up = () => setIsOnline(true);
    const dn = () => setIsOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', dn);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', dn);
    };
  }, []);

  // Re-read navigator.onLine when the sheet opens (catches transitions that
  // occurred while the sheet was closed).
  React.useEffect(() => {
    if (open) setIsOnline(navigator.onLine);
  }, [open]);

  function handleFullscreen() {
    const el = iframeRef.current;
    if (!el) return;
    const rfs = el.requestFullscreen
      || el.webkitRequestFullscreen
      || el.mozRequestFullScreen
      || el.msRequestFullscreen;
    if (!rfs) return;
    try {
      // Standard requestFullscreen returns a Promise; the prefixed variants
      // (older iOS/Safari) return void — guard before calling .catch (MD-01).
      const ret = rfs.call(el);
      if (ret && typeof ret.catch === 'function') {
        ret.catch(() => { /* iOS Safari rejects — YouTube's own FS button is the fallback */ });
      }
    } catch (_e) {
      /* Sync throw (e.g. iOS Safari) — swallow; YouTube's own FS button is the fallback */
    }
  }

  // Embed URL built ONLY from the extracted id + integer start seconds.
  // The raw videoUrl string is never interpolated into src or href (XSS boundary).
  const embedSrc = parsed
    ? `https://www.youtube-nocookie.com/embed/${parsed.id}?start=${parsed.start}&rel=0&modestbranding=1`
    : null;

  // Offline fallback: youtu.be short link (also built from extracted id/start).
  const ytFallbackUrl = parsed
    ? `https://youtu.be/${parsed.id}${parsed.start ? '?t=' + parsed.start : ''}`
    : null;

  return (
    <LLSheet open={open} onClose={onClose} title={exerciseName} subtitle="EXERCISE VIDEO" maxHeight="75%">
      {!open ? null : !isOnline ? (
        /* Offline state: internet required + Open-in-YouTube external link */
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <LLIcon.video style={{ width: 32, height: 32, color: LL.fgFaint }} />
          </div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginBottom: 20, lineHeight: 1.45 }}>
            Internet required to play videos
          </div>
          {ytFallbackUrl && (
            <a
              href={ytFallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: LL.r.md,
                border: `1px solid ${LL.line}`,
                color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              <LLIcon.upRight /> Open in YouTube
            </a>
          )}
        </div>
      ) : parsed ? (
        /* Online + sheet open: iframe mounts here (lazy-mount — VID-04) */
        <div>
          <div style={{ position: 'relative', borderRadius: LL.r.md, overflow: 'hidden', marginBottom: 10 }}>
            <iframe
              ref={iframeRef}
              title={exerciseName ? `${exerciseName} exercise demo` : 'Exercise video'}
              src={embedSrc}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
              style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
            />
          </div>
          <button
            onClick={handleFullscreen}
            style={{
              width: '100%', padding: '10px 0', borderRadius: LL.r.md,
              border: `1px solid ${LL.line}`, background: 'transparent',
              color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 6,
            }}
          >
            <LLIcon.video style={{ width: 15, height: 15 }} /> Fullscreen
          </button>
        </div>
      ) : null}
    </LLSheet>
  );
}

export { LLSheet, ExerciseMenuSheet, NotesSheet, IntensitySheet, PlateCalcSheet, WarmupSheet, SubstituteSheet, SupersetSheet, WeightEditSheet, VideoPlayerSheet };
